import React, { useState, useEffect, useRef } from 'react';
import './Stats.css';

function Counter({ end, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let startTime = null;
          const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * end));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function Stats() {
  return (
    <section className="stats-section" id="stats">
      <div className="section-container">
        <div className="section-header">
          <span className="section-badge">Platform Stats</span>
          <h2 className="section-title">Built for Production</h2>
          <p className="section-desc">
            Battle-tested with comprehensive test suites, real-time monitoring,
            and deployed across Vercel's edge network for global availability.
          </p>
        </div>
        <div className="stats-grid">
          <div className="stat-card stat-card-lg">
            <div className="stat-icon-wrap" style={{ '--stat-color': '#10b981' }}>🤖</div>
            <div className="stat-number" style={{ color: '#10b981' }}><Counter end={48} /></div>
            <div className="stat-name">Autonomous Agents</div>
            <div className="stat-detail">6 per system × 8 systems</div>
          </div>
          <div className="stat-card stat-card-lg">
            <div className="stat-icon-wrap" style={{ '--stat-color': '#22d3ee' }}>✅</div>
            <div className="stat-number" style={{ color: '#22d3ee' }}><Counter end={96} /></div>
            <div className="stat-name">Tests Passing</div>
            <div className="stat-detail">12 per system × 8 systems</div>
          </div>
          <div className="stat-card stat-card-lg">
            <div className="stat-icon-wrap" style={{ '--stat-color': '#a78bfa' }}>🌐</div>
            <div className="stat-number" style={{ color: '#a78bfa' }}><Counter end={8} /></div>
            <div className="stat-name">Live Deployments</div>
            <div className="stat-detail">All on Vercel Edge</div>
          </div>
          <div className="stat-card stat-card-lg">
            <div className="stat-icon-wrap" style={{ '--stat-color': '#f97316' }}>🛡️</div>
            <div className="stat-number" style={{ color: '#f97316' }}><Counter end={48} /></div>
            <div className="stat-name">Circuit Breakers</div>
            <div className="stat-detail">Per-agent fault isolation</div>
          </div>
        </div>
        <div className="tech-strip">
          <div className="tech-item">
            <span className="tech-dot" style={{ background: '#3572A5' }} />
            Python
          </div>
          <div className="tech-item">
            <span className="tech-dot" style={{ background: '#f1e05a' }} />
            JavaScript
          </div>
          <div className="tech-item">
            <span className="tech-dot" style={{ background: '#e34c26' }} />
            HTML/CSS
          </div>
          <div className="tech-item">
            <span className="tech-dot" style={{ background: '#000' }} />
            Vercel
          </div>
          <div className="tech-item">
            <span className="tech-dot" style={{ background: '#f05032' }} />
            Git
          </div>
          <div className="tech-item">
            <span className="tech-dot" style={{ background: '#61dafb' }} />
            React
          </div>
        </div>
      </div>
    </section>
  );
}
